import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    ArrowLeft,
    Clock,
    AlertTriangle,
    Users,
    CheckCircle,
    X,
    MessageCircle,
} from "lucide-react";
import { useAuth } from "../../Contexts/AuthContext";
import { useEvents } from "../../Contexts/EventsContext";
import ProgressCircle from "./hackathon/ProgressCircle";
import TabButton from "./hackathon/TabButton";
import Resources from "./hackathon/Resources";
import Teams from "./hackathon/Teams";
import ApplicationForm from "./hackathon/application";
import Evaluation from "./hackathon/Evaluation";
import ApplicationStatus from "./hackathon/ApplicationStatus";
import Overview from "./hackathon/Overview";
import AIChatModal from "./hackathon/AIChatModal";
import { useClarity } from "../../hooks/useClarity";
import { useHackathonDashboard } from "../../hooks/hackathon/useDashboard";
import {
    trackHackathonEvent,
    trackButtonClick,
    trackNavigation,
    HACKATHON_EVENTS,
} from "../../Services/analytics";

// Assume configs.applicationDate is available via event.configs or similar
const HackathonDashboard = () => {
    const { userData } = useAuth();
    const clarity = useClarity();
    const location = useLocation();
    const eventId = new URLSearchParams(location.search).get('eventId') || new URLSearchParams(location.search).get('id') || location.state?.eventId;
    const navigate = useNavigate();
    const clarityInitialized = useRef(false);

    // UI state
    const [activeTab, setActiveTab] = useState("overview");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [expandedLesson, setExpandedLesson] = useState(null);
    const [showApplicationForm, setShowApplicationForm] = useState(false);
    const [applicationStep, setApplicationStep] = useState(1);
    const [showAIChatModal, setShowAIChatModal] = useState(false);

    // Get event data
    const { events } = useEvents();
    const event = events.find((e) => e.id === eventId);
    // Get application date config if available
    const applicationDate = event?.configs?.applicationDate;

    // Use the main dashboard hook
    const {
        // Team data
        currentTeam,
        teamMembers,
        isTeamLead,
        teamLeaderData,

        // Application data
        userApplication,
        evaluationData,
        applicationData,

        // Categories
        categories,

        //Mentors resources
        mentorResources,

        // Progress
        progressData,
        showApplication,
        applicationWindowStart,
        applicationWindowEnd,
        getProgressStatus,

        // Loading states
        isDataLoading,
        isSubmitting,

        // Error states
        error,

        // UI state
        successMessage,
        showDeadlineModal,

        // Handlers
        handleApplicationDataChange,
        handleApplicationSubmit,
        handleTeamUpdate,
        clearSuccessMessage,
    } = useHackathonDashboard(eventId, event);

    // Initialize analytics on component mount
    useEffect(() => {
        // Track hackathon dashboard access
        trackHackathonEvent(HACKATHON_EVENTS.DASHBOARD_ACCESSED, {
            event_id: eventId,
            user_id: userData?.uid,
            access_time: new Date().toISOString(),
        });

        // Track hackathon dashboard page view with Clarity (only once per component mount)
        if (!clarityInitialized.current) {
            clarity.event("hackathon_dashboard_viewed");
            clarity.tag("event_id", eventId || "unknown");

            // If user is authenticated, identify them in Clarity (only once)
            if (userData) {
                clarity.identify(
                    userData.uid || userData.email,
                    null,
                    `hackathon_${eventId}`,
                    userData.displayName || userData.email
                );
                clarity.tag("user_role", userData.role || "participant");
            }

            clarityInitialized.current = true;
        }
    }, [eventId, clarity, userData]);

    // Redirect away from application tab if user has already submitted
    useEffect(() => {
        if (userApplication && activeTab === "application") {
            setActiveTab("evaluation");
        }
    }, [userApplication, activeTab]);

    // Helper function to track tab changes with Clarity
    const handleTabChange = (tabName) => {
        setActiveTab(tabName);

        // Only track critical tab changes
        if (tabName === "application") {
            clarity.event(`hackathon_${tabName}_accessed`);
            trackHackathonEvent(HACKATHON_EVENTS.APPLICATION_STARTED, {
                event_id: eventId,
                user_id: userData?.uid,
            });
        } else if (tabName === "teams") {
            trackHackathonEvent(HACKATHON_EVENTS.TEAMS_TAB_ACCESSED, {
                event_id: eventId,
                user_id: userData?.uid,
            });
        } else if (tabName === "evaluation") {
            clarity.event(`hackathon_${tabName}_accessed`);
        }
    };

    const handleGoBack = () => {
        setShowApplicationForm(false);
        setActiveTab("overview");
    };

    const handleResourcesClick = () => {
        setShowApplicationForm(false);
        setActiveTab("resources");
    };

    const handleEvaluationClick = () => {
        setShowApplicationForm(false);
        setActiveTab("evaluation");
    };

    const handleApplicationClick = () => {
        // Don't allow clicking if user has already submitted application
        if (!userApplication) {
            setShowApplicationForm(false);
            setActiveTab("application");
        }
    };

    const handleApplicationStepChange = (step) => {
        setApplicationStep(step);
    };

    const handleApplicationSubmitWithUI = async () => {
        const result = await handleApplicationSubmit();

        if (result.success) {
            setShowApplicationForm(false);
            setActiveTab("overview");
        }
    };

    if (!event) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Event not found
                    </h2>
                    <button
                        onClick={() => navigate("/")}
                        className="px-6 py-3 bg-custom-blue-1 text-white rounded-lg"
                    >
                        Back to Events
                    </button>
                </div>
            </div>
        );
    }

    // Helper to format Firestore Timestamp, Date, or string
    const formatDate = (date) => {
        if (!date) return '';
        if (date.toDate && typeof date.toDate === 'function') {
            return date.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        if (date instanceof Date) {
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        if (typeof date === 'string') {
            const d = new Date(date);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            }
            return date;
        }
        return String(date);
    };

    // Determine if application window is upcoming (prefer dates from hook if available)
    let isApplicationUpcoming = false;
    // Prefer applicationWindowStart provided by the hook; fall back to event.configs
    const appWindowStart = applicationWindowStart || applicationDate?.startDate;
    const appWindowEnd = applicationWindowEnd || applicationDate?.endDate;
    if (appWindowStart) {
        const now = new Date();
        let start = appWindowStart;
        if (start.toDate && typeof start.toDate === 'function') start = start.toDate();
        else if (start.seconds) start = new Date(start.seconds * 1000);
        else if (typeof start === 'string') start = new Date(start);
        isApplicationUpcoming = now < start;
    }

    // Resources progress helper (percentage and completed flag)
    const resourcesProgress = progressData?.find(p => p.title === "Resources")?.percentage ?? 0;
    const resourcesCompleted = resourcesProgress === 100;

    return (
        <div className="min-h-screen relative">
            {/* Success Message */}
            {successMessage && (
                <div className="fixed top-4 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-lg max-w-md">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        <span className="text-sm font-medium">{successMessage}</span>
                        <button
                            onClick={clearSuccessMessage}
                            className="ml-4 text-green-700 hover:text-green-900 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto mt-2 px-2 py-3 sm:px-2 sm:py-2 lg:px-5 lg:py-7">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Events
                </button>

                {/* Combined Progress and Content Card */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-xl border border-white/20 overflow-hidden">
                    {/* Fixed Progress Section */}
                    <div className="sticky top-0 z-10 relative bg-custom-blue-1/95 backdrop-blur-sm p-4 sm:p-8 border-b border-white/20 overflow-hidden">
                        {/* Background Image */}
                        <div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
                            style={{
                                backgroundImage: `url('https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80')`,
                            }}
                        />
                        {/* Blue Overlay */}
                        <div className="absolute inset-0 bg-custom-blue-1/70" />

                        {/* Content */}
                        <div className="relative z-10">
                            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-8 text-center">
                                Your Progress
                            </h2>
                            <div className="flex justify-center sm:justify-center items-center gap-4 sm:gap-16 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                                {isDataLoading
                                    ? // Skeleton loading for progress circles
                                    [...Array(3)].map((_, index) => (
                                        <div
                                            key={index}
                                            className="flex flex-col items-center min-w-[100px] sm:min-w-[120px]"
                                        >
                                            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-white/20 animate-pulse mb-2 sm:mb-4"></div>
                                            <div className="w-16 sm:w-20 h-3 sm:h-4 bg-white/20 rounded animate-pulse mb-1 sm:mb-2"></div>
                                            <div className="w-12 sm:w-16 h-2 sm:h-3 bg-white/20 rounded animate-pulse"></div>
                                        </div>
                                    ))
                                    : progressData.map((item, index) => (
                                        <ProgressCircle
                                            key={index}
                                            percentage={item.percentage}
                                            title={item.title}
                                            status={getProgressStatus(item.title)}
                                            isActive={
                                                (item.title === "Resources" &&
                                                    activeTab === "resources") ||
                                                (item.title === "Application" &&
                                                    (activeTab === "application" ||
                                                        showApplicationForm)) ||
                                                (item.title === "Evaluation" &&
                                                    activeTab === "evaluation")
                                            }
                                            onClick={
                                                item.title === "Application" && !userApplication && showApplication
                                                    ? handleApplicationClick
                                                    : item.title === "Resources"
                                                        ? handleResourcesClick
                                                        : item.title === "Evaluation" && userApplication
                                                            ? handleEvaluationClick
                                                            : undefined
                                            }
                                        />
                                    ))}
                            </div>
                        </div>
                    </div>

                    {/* Conditionally show tabs or application form */}
                    {!showApplicationForm && (
                        <>
                            {/* Tabs */}
                            <div className="border-b border-white/20 bg-black/20 backdrop-blur-sm">
                                <div className="flex overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                                    <TabButton
                                        active={activeTab === "overview"}
                                        onClick={() => handleTabChange("overview")}
                                    >
                                        Overview
                                    </TabButton>
                                    <TabButton
                                        active={activeTab === "teams"}
                                        onClick={() => handleTabChange("teams")}
                                    >
                                        Teams
                                    </TabButton>
                                    {(event?.eventType === "hackathon" ||
                                        event?.eventType === "grant") && (
                                            <TabButton
                                                active={activeTab === "resources"}
                                                onClick={() => handleTabChange("resources")}
                                            >
                                                Resources
                                            </TabButton>
                                        )}
                                    {/* Only show Application tab if showApplication is true and user has not applied */}
                                    {showApplication && !userApplication && (
                                        <TabButton
                                            active={activeTab === "application"}
                                            onClick={() => handleTabChange("application")}
                                        >
                                            Application
                                        </TabButton>
                                    )}
                                    {userApplication && (
                                        <TabButton
                                            active={activeTab === "evaluation"}
                                            onClick={() => handleTabChange("evaluation")}
                                        >
                                            Evaluation
                                        </TabButton>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 ml-auto mx-2 mt-2 mb-2">
                                        <button
                                            onClick={() => setShowAIChatModal(true)}
                                            className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-custom-blue-1 hover:bg-blue-700 rounded-lg transition-colors"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                            Get Help
                                        </button>
                                        <a
                                            href="https://chat.whatsapp.com/BMkf6J2tFgYFWrLELbV4zn"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-custom-green-1 hover:bg-custom-green-1/90 rounded-lg transition-colors"
                                        >
                                            Support
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-8 bg-white rounded-b-xl">
                                {activeTab === "overview" && <Overview event={event} />}
                                {activeTab === "resources" &&
                                    (event?.eventType === "hackathon" ||
                                        event?.eventType === "grant") && (
                                        <Resources
                                            event={event}
                                            selectedDate={selectedDate}
                                            setSelectedDate={setSelectedDate}
                                            expandedResource={expandedLesson}
                                            setExpandedResource={setExpandedLesson}
                                            currentTeam={currentTeam}
                                            mentorResources={mentorResources}
                                            onSwitchToTeams={() => setActiveTab("teams")}
                                            onTeamUpdate={handleTeamUpdate}
                                        />
                                    )}
                                {activeTab === "application" && !userApplication && (
                                    <ApplicationForm
                                        applicationData={applicationData}
                                        applicationStep={applicationStep}
                                        isLoading={isSubmitting}
                                        error={error}
                                        onDataChange={handleApplicationDataChange}
                                        onStepChange={handleApplicationStepChange}
                                        onSubmit={handleApplicationSubmitWithUI}
                                        onGoBack={() => setActiveTab("overview")}
                                        currentTeam={currentTeam}
                                        teamMembers={teamMembers}
                                        isTeamLead={isTeamLead}
                                        teamLeaderData={teamLeaderData}
                                        eventId={eventId}
                                        event={event}
                                        categories={categories}
                                        onGoToTeams={() => {
                                            setActiveTab("teams");
                                            setTimeout(() => {
                                                window.scrollTo({ top: 0, behavior: "smooth" });
                                            }, 100);
                                        }}
                                    />
                                )}
                                {activeTab === "teams" && (
                                    <Teams
                                        eventId={eventId}
                                        currentTeam={currentTeam}
                                        userApplication={userApplication}
                                        setCurrentTeam={() => { }} // Handled by hook now
                                        teamMembers={teamMembers}
                                        isTeamLead={isTeamLead}
                                        onTeamUpdate={handleTeamUpdate}
                                    />
                                )}
                                {activeTab === "evaluation" && userApplication && (
                                    <div>
                                        {/* Application Status Section */}
                                        <ApplicationStatus
                                            applicationData={userApplication}
                                            onViewEvaluation={() => { }}
                                        />

                                        {/* Evaluation Section - only show if evaluated */}
                                        {evaluationData && (
                                            <div className="mt-8">
                                                <Evaluation
                                                    evaluationData={evaluationData}
                                                    applicationData={userApplication}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Application Form */}
                    {showApplicationForm && (
                        <div className="p-8 bg-white rounded-b-xl">
                            <ApplicationForm
                                applicationData={applicationData}
                                applicationStep={applicationStep}
                                isLoading={isSubmitting}
                                error={error}
                                onDataChange={handleApplicationDataChange}
                                onStepChange={handleApplicationStepChange}
                                onSubmit={handleApplicationSubmitWithUI}
                                onGoBack={handleGoBack}
                                currentTeam={currentTeam}
                                teamMembers={teamMembers}
                                isTeamLead={isTeamLead}
                                categories={categories}
                                onGoToTeams={() => {
                                    setShowApplicationForm(false);
                                    setActiveTab("teams");
                                    setTimeout(() => {
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }, 100);
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Bottom sticky CTA with countdown urgency */}
                {!userApplication && !isDataLoading && (
                    <div className="hidden lg:flex fixed left-0 right-0 bottom-8 justify-center pointer-events-none z-50">
                        <div className="max-w-4xl w-full px-6 pointer-events-auto">
                            <div className="bg-white rounded-2xl p-4 shadow-2xl border-2 border-gray-200 animate-pulse">
                                <div className="flex items-center justify-between">
                                    {!currentTeam ? (
                                        <>
                                            <div className="flex items-center gap-6 px-4">
                                                <div className="w-14 h-14 rounded-full bg-custom-blue-1/20 flex items-center justify-center">
                                                    <Users className="w-7 h-7 text-custom-blue-1" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-custom-blue-1 uppercase tracking-wide">
                                                        Welcome to Industry 4.0+ Hackathon
                                                    </div>
                                                    <div className="font-bold text-gray-900 text-lg">
                                                        Please proceed to create a team
                                                    </div>
                                                    <div className="text-sm text-gray-600 mt-1">
                                                        Join or create your team to get started with the hackathon journey
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="px-4">
                                                <button
                                                    onClick={() => setActiveTab("teams")}
                                                    className="py-4 px-8 rounded-full bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Users className="w-5 h-5" />
                                                        <span>Create Team</span>
                                                    </div>
                                                </button>
                                            </div>
                                        </>
                                    ) : !isTeamLead ? (
                                        // Team member view: show status based on resources and application window
                                        resourcesCompleted && showApplication ? (
                                            // Resources done and applications open -> tell member to ask leader
                                            <>
                                                <div className="flex items-center gap-6 px-4">
                                                    <div className="w-14 h-14 rounded-full bg-custom-blue-1/20 flex items-center justify-center">
                                                        <Users className="w-7 h-7 text-custom-blue-1" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-custom-blue-1 uppercase tracking-wide">
                                                            Team Member
                                                        </div>
                                                        <div className="font-bold text-gray-900 text-lg">
                                                            Your team is ready to apply
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Ask your team leader to submit the application now.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4">
                                                    <button
                                                        onClick={() => setActiveTab("teams")}
                                                        className="py-4 px-8 rounded-full bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Users className="w-5 h-5" />
                                                            <span>View Team</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        ) : resourcesCompleted && !showApplication ? (
                                            // Resources done but applications not yet open -> show upcoming window
                                            <>
                                                <div className="flex items-center gap-6 px-4">
                                                    <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <Clock className="w-7 h-7 text-blue-500" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
                                                            Applications Not Yet Open
                                                        </div>
                                                        <div className="font-bold text-gray-900 text-lg">
                                                            Applications will be open from {formatDate(appWindowStart)} to {formatDate(appWindowEnd)}
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Please check back when applications open.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4">
                                                    <button
                                                        onClick={() => setActiveTab("resources")}
                                                        className="py-4 px-8 rounded-full bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Users className="w-5 h-5" />
                                                            <span>View Resources</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            // Resources not complete -> prompt to view resources and show dates if available
                                            <>
                                                <div className="flex items-center gap-6 px-4">
                                                    <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                                                        <Users className="w-7 h-7 text-orange-500" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-orange-600 uppercase tracking-wide">
                                                            Complete Your Resources
                                                        </div>
                                                        <div className="font-bold text-gray-900 text-lg">
                                                            Please complete all assigned resources
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Finish the resources to unlock the application process. Applications are scheduled {appWindowStart ? `from ${formatDate(appWindowStart)} to ${formatDate(appWindowEnd)}` : "during the application window."}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4">
                                                    <button
                                                        onClick={() => setActiveTab("resources")}
                                                        className="py-4 px-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Users className="w-5 h-5" />
                                                            <span>View Resources</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        )
                                    ) : (
                                        // Team leader view
                                        resourcesCompleted && showApplication ? (
                                            <>
                                                <div className="flex items-center gap-6 px-4">
                                                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                                                        <CheckCircle className="w-7 h-7 text-green-500" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-green-600 uppercase tracking-wide">
                                                            Apply Now for Industry 4.0+ Hackathon
                                                        </div>
                                                        <div className="font-bold text-gray-900 text-lg">
                                                            Submit your application and join the innovation
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Your team has completed the resources and can apply now.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4">
                                                    <button
                                                        onClick={handleApplicationClick}
                                                        className="group relative py-4 px-8 rounded-full bg-custom-green-1 hover:bg-custom-green-1/90 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-custom-green-1/30"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <CheckCircle className="w-5 h-5" />
                                                            <span>Apply Now!</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        ) : resourcesCompleted && !showApplication ? (
                                            <>
                                                <div className="flex items-center gap-6 px-4">
                                                    <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <Clock className="w-7 h-7 text-blue-500" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
                                                            Applications Not Yet Open
                                                        </div>
                                                        <div className="font-bold text-gray-900 text-lg">
                                                            Applications will be open from {formatDate(appWindowStart)} to {formatDate(appWindowEnd)}
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Please check back when the application window opens.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4">
                                                    <button
                                                        onClick={() => setActiveTab("resources")}
                                                        className="py-4 px-8 rounded-full bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Users className="w-5 h-5" />
                                                            <span>View Resources</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-6 px-4">
                                                    <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                                                        <Users className="w-7 h-7 text-orange-500" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-orange-600 uppercase tracking-wide">
                                                            Complete Your Resources
                                                        </div>
                                                        <div className="font-bold text-gray-900 text-lg">
                                                            Please complete all resources assigned by your mentors
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Finish all assigned resources to unlock the application process{appWindowStart ? ` (applications: ${formatDate(appWindowStart)} - ${formatDate(appWindowEnd)})` : '.'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4">
                                                    <button
                                                        onClick={() => setActiveTab("resources")}
                                                        className="py-4 px-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-xl transform hover:scale-105 transition-all duration-300"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Users className="w-5 h-5" />
                                                            <span>View Resources</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile CTA */}
                {!userApplication && !isDataLoading && (
                    <div className="lg:hidden fixed left-0 right-0 bottom-4 px-4 pointer-events-none z-50">
                        <div className="pointer-events-auto">
                            <div className="bg-white rounded-xl p-3 shadow-xl border-2 border-gray-200">
                                {!currentTeam ? (
                                    <div className="text-center">
                                        <div className="text-xs font-semibold text-custom-blue-1 uppercase tracking-wide mb-1">
                                            Welcome to Industry 4.0+ Hackathon
                                        </div>
                                        <div className="text-sm font-bold text-gray-900 mb-2">
                                            Please proceed to create a team
                                        </div>
                                        <div className="text-xs text-gray-600 mb-3">
                                            Join or create your team to get started
                                        </div>
                                        <button
                                            onClick={() => setActiveTab("teams")}
                                            className="w-full py-3 px-6 rounded-full bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-bold shadow-lg"
                                        >
                                            Create Team
                                        </button>
                                    </div>
                                ) : !isTeamLead ? (
                                    <div className="text-center">
                                        <div className="text-xs font-semibold text-custom-blue-1 uppercase tracking-wide mb-1">
                                            Team Member
                                        </div>
                                        <div className="text-sm font-bold text-gray-900 mb-2">
                                            Only team leaders can submit applications
                                        </div>
                                        <div className="text-xs text-gray-600 mb-3">
                                            Team leader:{" "}
                                            <span className="font-semibold">
                                                {teamLeaderData?.displayName || "Team Leader"}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => setActiveTab("teams")}
                                            className="w-full py-3 px-6 rounded-full bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-bold shadow-lg"
                                        >
                                            View Team
                                        </button>
                                    </div>
                                ) : showApplication ? (
                                    <div className="text-center">
                                        <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">
                                            Apply Now for Industry 4.0+ Hackathon
                                        </div>
                                        <div className="text-sm font-bold text-gray-900 mb-2">
                                            Submit your application and join the innovation
                                        </div>
                                        <div className="text-xs text-gray-600 mb-3">
                                            Complete your application to participate
                                        </div>
                                        <button
                                            onClick={handleApplicationClick}
                                            className="w-full py-3 px-6 rounded-full bg-custom-green-1 hover:bg-custom-green-1/90 text-white font-bold shadow-lg"
                                        >
                                            Apply Now!
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <div className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">
                                            Complete Your Resources
                                        </div>
                                        <div className="text-sm font-bold text-gray-900 mb-2">
                                            Please complete all resources assigned by your mentors
                                        </div>
                                        <div className="text-xs text-gray-600 mb-3">
                                            Finish all assigned resources to unlock application
                                        </div>
                                        <button
                                            onClick={() => setActiveTab("resources")}
                                            className="w-full py-3 px-6 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg"
                                        >
                                            View Resources
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Deadline Modal */}
                {showDeadlineModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl p-8 max-w-md mx-4 text-center shadow-2xl">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                                <AlertTriangle className="w-8 h-8 text-red-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Registration and Team Creation Deadline Reached
                            </h3>
                            <p className="text-gray-600 mb-6">
                                The registration and team creation deadline for <strong>{event?.title}</strong> has
                                been reached. You can no longer create teams or submit applications for this
                                event.
                            </p>
                            <div className="text-sm text-gray-500 mb-6">
                                Deadline was:{" "}
                                <strong>
                                    {new Date(event?.deadline).toLocaleDateString()}
                                </strong>
                            </div>
                            <button
                                onClick={() => navigate("/")}
                                className="w-full py-3 px-6 bg-custom-blue-1 hover:bg-custom-blue-1/90 text-white font-semibold rounded-lg transition-colors"
                            >
                                Back to Events
                            </button>
                        </div>
                    </div>
                )}

                {/* AI Chat Modal */}
                <AIChatModal
                    isOpen={showAIChatModal}
                    onClose={() => setShowAIChatModal(false)}
                />
            </div>
        </div>
    );
}

export default HackathonDashboard;